import React from 'react';
import { FeatherLock } from '@subframe/core';
import { useAuthStore } from '../store/authStore';
import { useSignInModalStore } from '../store/signInModalStore';
import { Button } from '../ui/components/Button';

interface BlurredPremiumFeatureProps {
  children: React.ReactNode;
  /** Label shown in the "Sign in to view" overlay */
  label?: string;
  /** Additional classes for the wrapper */
  className?: string;
  /** Use compact mode for small containers (no icon, smaller text) */
  compact?: boolean;
  /** Use inline mode for tiny inline badges (just lock icon) */
  inline?: boolean;
}

/**
 * Wraps premium content with a blur effect for logged-out users.
 * Shows real data behind the blur to create intrigue, with a "Sign in to view" overlay.
 */
export const BlurredPremiumFeature: React.FC<BlurredPremiumFeatureProps> = ({
  children,
  label = 'this data',
  className = '',
  compact = false,
  inline = false,
}) => {
  const { isAuthenticated } = useAuthStore();
  const openSignIn = useSignInModalStore((st) => st.open);

  // For authenticated users, render children normally
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Inline mode - minimal lock icon for tiny inline badges
  if (inline) {
    return (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openSignIn();
          }}
          className={`inline-flex items-center gap-1 text-brand-primary hover:text-brand-600 cursor-pointer ${className}`}
          title="Sign in to view"
        >
          <FeatherLock className="w-3 h-3" />
        </button>
    );
  }

  // Compact mode - simple text message for small containers (no click action to avoid nested interactive elements)
  if (compact) {
    return (
      <div className={`relative rounded-md overflow-hidden ${className}`}>
        {/* Blurred content */}
        <div className="blur-sm select-none pointer-events-none opacity-40">
          {children}
        </div>

        {/* Compact overlay - plain text message */}
        <div className="absolute inset-0 flex items-center justify-start bg-default-background/70">
          <span className="text-caption font-caption text-subtext-color">
            Sign in for details
          </span>
        </div>
      </div>
    );
  }

  return (
      <div className={`relative rounded-md overflow-hidden ${className}`}>
        {/* Blurred content - actual data is rendered but blurred */}
        <div className="blur-sm select-none pointer-events-none opacity-40">
          {children}
        </div>

        {/* Overlay with sign-in prompt */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-default-background/80 backdrop-blur-[1px]">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand-100 border border-brand-200">
            <FeatherLock className="w-5 h-5 text-brand-600" />
          </div>
          <div className="flex flex-col items-center gap-2 px-4">
            <span className="text-body-bold font-body-bold text-default-font text-center">
              Sign in to view {label}
            </span>
            <Button
              variant="brand-primary"
              size="small"
              onClick={openSignIn}
            >
              Sign In
            </Button>
          </div>
        </div>
      </div>
  );
};
